// src/components/Finanzas/subtabs/ManifiestoTab.jsx
// Finanzas → Manifiesto: billing coverage first, then money, then the invoice drill.
//
// Every figure is a column from the v_finanzas_* views — counts included.
// v_finanzas_manifiesto.facturas excludes voided invoices while the invoice rows
// include them (decision F4: anuladas are counted, never summed), so never show
// a row count as an invoice count.

import React, { useMemo, useState } from 'react';
import {
  useManifiestosFinanzas,
  useManifiestoFinanzas,
  useFinanzasExcepciones,
  fetchCajaContenido,
  VIEWS_MISSING,
} from '../hooks/useFinanzasData';
import {
  fmtUsd,
  fmtPct,
  fmtLb,
  fmtRate,
  tipoMeta,
  statusMeta,
  documentoLabel,
  TIPO_ORDER,
  DESGLOSE_META,
  DESGLOSE_ORDER,
  INGRESO_VS_PASANTES_NOTE,
} from '../utils/facturaMath';
import { formatSvDate } from '../utils/timeBoundaries';

// FOB is null for casillero packages: show a dash, never $0.00.
const usdOrDash = (v) => (v === null || v === undefined ? '—' : fmtUsd(v));
const textOrDash = (v) => (v === null || v === undefined || v === '' ? '—' : v);
const cajaKey = (f) => `${f.box_id}:${f.customer_id}`;

// The manifest view's count for each tipo bucket.
const TIPO_COUNT_COLUMN = {
  casillero:      'facturas_casillero',
  encomienda:     'facturas_encomienda',
  sin_clasificar: 'facturas_sin_tipo',
};

const TipoChip = ({ tipo }) => {
  const meta = tipoMeta(tipo);
  return (
    <span className="fin-chip" style={{ background: `${meta.color}1f`, color: meta.color, borderColor: `${meta.color}66` }}>
      {meta.label}
    </span>
  );
};

const StatusChip = ({ status }) => {
  const meta = statusMeta(status);
  return <span className="fin-chip" style={{ background: meta.bg, color: meta.color }}>{meta.label}</span>;
};

const PasantesInfo = () => (
  <span className="fin-info" title={INGRESO_VS_PASANTES_NOTE} aria-label={INGRESO_VS_PASANTES_NOTE} role="img">i</span>
);

const ErrorBanner = ({ message, onRetry }) => (
  <div className="fin-banner fin-banner--error" role="alert">
    <span>{message}</span>
    {onRetry && <button type="button" className="fin-btn" onClick={onRetry}>Reintentar</button>}
  </div>
);

// Pick the OPEN manifest; else the most recently closed; else the newest listed.
const pickDefaultManifest = (rows) => {
  if (!rows || rows.length === 0) return null;
  const open = rows.find((r) => r.manifiesto_status === 'OPEN');
  if (open) return open.manifest_id;
  let latestClosed = null;
  for (const r of rows) {
    if (r.manifiesto_status !== 'CLOSED' || !r.cerrado) continue;
    if (!latestClosed || r.cerrado > latestClosed.cerrado) latestClosed = r;
  }
  return (latestClosed || rows[0]).manifest_id;
};

const manifestOptionLabel = (m) => {
  const estado = m.manifiesto_status === 'OPEN'
    ? `Abierto desde ${formatSvDate(m.abierto)}`
    : `Cerrado ${formatSvDate(m.cerrado)}`;
  return `${m.manifest_id} — ${estado}`;
};

// ── Cards ─────────────────────────────────────────────────────────────────────

const CoberturaCard = ({ m }) => {
  const sinCajas = !(Number(m.tripletas) > 0);
  const incompleta = !sinCajas && Number(m.cobertura) < 1;
  const tone = sinCajas ? '' : incompleta ? 'fin-card--warn' : 'fin-card--ok';
  return (
    <div className={`fin-card fin-card--headline ${tone}`}>
      <div className="fin-card-label">Cobertura de facturación</div>
      <div className="fin-card-value">{sinCajas ? '—' : fmtPct(m.cobertura)}</div>
      {sinCajas ? (
        <div className="fin-card-sub">Este manifiesto todavía no tiene cajas.</div>
      ) : (
        <>
          <div className="fin-card-sub">
            {m.tripletas_facturadas} de {m.tripletas} cajas por cliente facturadas
          </div>
          <div className="fin-card-sub">{fmtLb(m.lb_sin_facturar)} sin facturar</div>
          {incompleta && (
            <div className="fin-card-alert">Hay cajas sin factura — cada una es carga que sale sin cobrarse.</div>
          )}
        </>
      )}
    </div>
  );
};

const IngresoCard = ({ m }) => (
  <div className="fin-card">
    <div className="fin-card-label">Ingreso propio</div>
    <div className="fin-card-value">{fmtUsd(m.ingreso)}</div>
    <div className="fin-card-sub">Flete {fmtUsd(m.flete)} · Descuentos {fmtUsd(m.descuento)}</div>
    <div className="fin-card-sub">Tarifa {fmtRate(m.tarifa_lb)} · {fmtLb(m.lb_facturadas)} facturadas</div>
  </div>
);

const PasantesCard = ({ m }) => (
  <div className="fin-card" title={INGRESO_VS_PASANTES_NOTE}>
    <div className="fin-card-label">Pasantes <PasantesInfo /></div>
    <div className="fin-card-value">{fmtUsd(m.pasantes)}</div>
    <div className="fin-card-sub">DAI + IVA por cuenta de la DGA</div>
  </div>
);

const CobroCard = ({ m }) => (
  <div className="fin-card">
    <div className="fin-card-label">Cobrado / Por cobrar</div>
    <div className="fin-split">
      <div>
        <div className="fin-card-value" style={{ color: '#15803d' }}>{fmtUsd(m.cobrado)}</div>
        <div className="fin-card-sub">Cobrado</div>
      </div>
      <div>
        <div className="fin-card-value" style={{ color: '#c2410c' }}>{fmtUsd(m.por_cobrar)}</div>
        <div className="fin-card-sub">Por cobrar</div>
      </div>
    </div>
    <div className="fin-card-sub">Facturado {fmtUsd(m.facturado)}</div>
  </div>
);

const TipoSplitCard = ({ m }) => (
  <div className="fin-card">
    <div className="fin-card-label">Casillero vs Encomienda</div>
    <div className="fin-tipo-list">
      {TIPO_ORDER.map((t) => (
        <div key={t} className="fin-tipo-line">
          <TipoChip tipo={t} />
          <strong>{m[TIPO_COUNT_COLUMN[t]] ?? 0}</strong>
        </div>
      ))}
    </div>
    <div className="fin-card-sub" style={{ marginTop: '8px' }}>
      Facturas activas <strong>{m.facturas ?? 0}</strong> · Anuladas <strong>{m.anuladas ?? 0}</strong>
    </div>
  </div>
);

// ── Drill level 3: box contents ───────────────────────────────────────────────

const CajaContenido = ({ entry }) => {
  if (!entry || entry.loading) return <div className="fin-muted">Cargando contenido de la caja…</div>;
  if (entry.error) return <ErrorBanner message={entry.error} />;
  if (entry.viewsMissing) return <div className="fin-banner fin-banner--warn">Las vistas de Finanzas no están disponibles. Contacta al administrador.</div>;
  if (!entry.rows || entry.rows.length === 0) return <div className="fin-muted">No hay paquetes registrados para este cliente en esta caja.</div>;

  return (
    <div className="fin-table">
      <div className="fin-tr fin-tr--head fin-cols-caja">
        <div className="fin-td">#</div>
        <div className="fin-td">Paquete</div>
        <div className="fin-td">Tracking</div>
        <div className="fin-td">Destinatario</div>
        <div className="fin-td fin-td--num">Peso</div>
        <div className="fin-td fin-td--num">FOB</div>
        <div className="fin-td">Documento</div>
      </div>
      {entry.rows.map((p) => (
        <div key={p.package_ref_id || `${p.paquete_id}-${p.numero_paquete}`} className="fin-tr fin-cols-caja">
          <div className="fin-td" data-label="#">{textOrDash(p.numero_paquete)}</div>
          <div className="fin-td" data-label="Paquete">
            {textOrDash(p.paquete_id)} <TipoChip tipo={p.tipo} />
          </div>
          <div className="fin-td" data-label="Tracking">{textOrDash(p.tracking)}</div>
          <div className="fin-td" data-label="Destinatario">{textOrDash(p.destinatario)}</div>
          <div className="fin-td fin-td--num" data-label="Peso">{fmtLb(p.peso_lb)}</div>
          <div className="fin-td fin-td--num" data-label="FOB">{usdOrDash(p.fob)}</div>
          <div className="fin-td" data-label="Documento">
            {documentoLabel(p.tipo)}:{' '}
            {p.cumple_documento === true && <span className="fin-doc-ok">Sí</span>}
            {p.cumple_documento === false && <span className="fin-doc-missing">Falta</span>}
            {p.cumple_documento !== true && p.cumple_documento !== false && '—'}
          </div>
        </div>
      ))}
    </div>
  );
};

// ── Drill level 2: invoice breakdown ──────────────────────────────────────────

const FacturaDetalle = ({ f, cajaOpen, onToggleCaja, cajaEntry }) => {
  const lineas = DESGLOSE_ORDER.filter((k) => f[k] !== null && f[k] !== undefined && Number(f[k]) !== 0);
  return (
    <div className="fin-drill">
      <div className="fin-drill-grid">
        <div>
          <div className="fin-drill-title">Desglose por tipo de línea</div>
          {lineas.length === 0 && <div className="fin-muted" style={{ padding: 0 }}>Sin líneas registradas.</div>}
          {lineas.map((k) => (
            <div key={k} className={`fin-kv ${DESGLOSE_META[k].pasante ? 'fin-kv--pasante' : ''}`}>
              <span>{DESGLOSE_META[k].label}{DESGLOSE_META[k].pasante ? ' (pasante)' : ''}</span>
              <span>{fmtUsd(f[k])}</span>
            </div>
          ))}
          <div className="fin-kv fin-kv--total"><span>Ingreso propio</span><span>{fmtUsd(f.ingreso)}</span></div>
          <div className="fin-kv" title={INGRESO_VS_PASANTES_NOTE}><span>Pasantes <PasantesInfo /></span><span>{fmtUsd(f.pasantes)}</span></div>
          <div className="fin-kv fin-kv--total"><span>Total</span><span>{fmtUsd(f.total)}</span></div>
          <div className="fin-kv"><span>Cobrado</span><span>{fmtUsd(f.cobrado)}</span></div>
          <div className="fin-kv"><span>Saldo</span><span>{fmtUsd(f.saldo)}</span></div>
        </div>
        <div>
          <div className="fin-drill-title">Datos de la factura</div>
          <div className="fin-kv"><span>Emitida</span><span>{formatSvDate(f.fecha_emision)}</span></div>
          <div className="fin-kv"><span>Cobrada</span><span>{formatSvDate(f.fecha_cobro)}</span></div>
          <div className="fin-kv"><span>Peso</span><span>{fmtLb(f.peso_lb)}</span></div>
          <div className="fin-kv"><span>Paquetes</span><span>{textOrDash(f.paquetes)}</span></div>
          <div className="fin-kv"><span>Tarifa</span><span>{fmtRate(f.tarifa_lb)}</span></div>
          <div className="fin-kv"><span>Valor declarado</span><span>{usdOrDash(f.valor_declarado)}</span></div>
          <div className="fin-kv"><span>Pagos</span><span>{f.n_pagos ?? 0}{Number(f.n_pagos_anulados) > 0 ? ` (+${f.n_pagos_anulados} anulados)` : ''}</span></div>
          <div className="fin-kv"><span>Operador</span><span>{textOrDash(f.operador)}</span></div>
          <div className="fin-kv"><span>Caja</span><span>{textOrDash(f.box_id)}</span></div>
        </div>
      </div>

      <div className="fin-caja">
        <button type="button" className="fin-btn" aria-expanded={cajaOpen} onClick={onToggleCaja}>
          {cajaOpen ? '▾ Ocultar contenido de la caja' : '▸ Ver contenido de la caja'}
        </button>
        {cajaOpen && <div style={{ marginTop: '10px' }}><CajaContenido entry={cajaEntry} /></div>}
      </div>
    </div>
  );
};

// ── Tab ───────────────────────────────────────────────────────────────────────

const ManifiestoTab = () => {
  const lista = useManifiestosFinanzas(24);
  const [selectedId, setSelectedId] = useState(null);
  const defaultId = useMemo(() => pickDefaultManifest(lista.data), [lista.data]);
  const manifestId = selectedId || defaultId;

  const detalle = useManifiestoFinanzas(manifestId);
  const excepciones = useFinanzasExcepciones();

  const [openFacturas, setOpenFacturas] = useState({}); // invoice id → true
  const [openCajas, setOpenCajas] = useState({});       // invoice id → true
  const [cajas, setCajas] = useState({});               // "box:client" → { loading, error, rows, viewsMissing }

  const onSelectManifest = (id) => {
    setSelectedId(id);
    setOpenFacturas({});
    setOpenCajas({});
  };

  const toggleFactura = (id) => setOpenFacturas((prev) => ({ ...prev, [id]: !prev[id] }));

  // Contents load on the first open of a box and stay cached; reopening never refetches.
  const toggleCaja = async (f) => {
    const opening = !openCajas[f.id];
    setOpenCajas((prev) => ({ ...prev, [f.id]: opening }));
    const key = cajaKey(f);
    // A failed load stays visible until the box is opened again, which retries it.
    if (!opening || (cajas[key] && !cajas[key].error)) return;
    setCajas((prev) => ({ ...prev, [key]: { loading: true } }));
    try {
      const result = await fetchCajaContenido(f.box_id, f.customer_id);
      setCajas((prev) => ({
        ...prev,
        [key]: result === VIEWS_MISSING
          ? { loading: false, viewsMissing: true }
          : { loading: false, rows: result },
      }));
    } catch (err) {
      console.error('[FINANZAS] caja contenido:', err);
      setCajas((prev) => ({
        ...prev,
        [key]: { loading: false, error: 'No se pudo cargar el contenido de la caja. Cerrá y volvé a abrir para reintentar.' },
      }));
    }
  };

  if (lista.viewsMissing || detalle.viewsMissing || excepciones.viewsMissing) {
    return <div className="fin-banner fin-banner--warn">Las vistas de Finanzas no están disponibles. Contacta al administrador.</div>;
  }

  if (lista.error) return <ErrorBanner message={lista.error} onRetry={lista.refetch} />;
  if (lista.loading && !lista.data) return <div className="fin-muted">Cargando manifiestos…</div>;
  if (!lista.data || lista.data.length === 0) return <div className="fin-muted">Todavía no hay manifiestos.</div>;

  const m = detalle.data?.manifiesto;
  const facturas = detalle.data?.facturas || [];
  const excepcionesManifiesto = (excepciones.data || []).filter((e) => e.manifest_id === manifestId);

  return (
    <div>
      <div className="fin-selector">
        <label htmlFor="fin-manifiesto">Manifiesto</label>
        <select id="fin-manifiesto" value={manifestId || ''} onChange={(e) => onSelectManifest(e.target.value)}>
          {lista.data.map((row) => (
            <option key={row.manifest_id} value={row.manifest_id}>{manifestOptionLabel(row)}</option>
          ))}
        </select>
        {detalle.loading && <span className="fin-muted" style={{ padding: 0 }}>Actualizando…</span>}
      </div>

      {detalle.error && <ErrorBanner message={detalle.error} onRetry={detalle.refetch} />}
      {!detalle.error && !m && !detalle.loading && (
        <div className="fin-muted">No hay datos financieros para este manifiesto.</div>
      )}

      {m && (
        <>
          <div className="fin-cards">
            <CoberturaCard m={m} />
            <IngresoCard m={m} />
            <PasantesCard m={m} />
            <CobroCard m={m} />
            <TipoSplitCard m={m} />
          </div>

          <section className="fin-section">
            <h3 className="fin-section-title">Facturas del manifiesto</h3>
            {facturas.length === 0 ? (
              <div className="fin-muted">Este manifiesto todavía no tiene facturas.</div>
            ) : (
              <div className="fin-table">
                <div className="fin-tr fin-tr--head fin-cols-facturas">
                  <div className="fin-td" />
                  <div className="fin-td">Factura</div>
                  <div className="fin-td">Cliente</div>
                  <div className="fin-td">Tipo</div>
                  <div className="fin-td">Estado</div>
                  <div className="fin-td fin-td--num">Total</div>
                  <div className="fin-td fin-td--num">Saldo</div>
                </div>
                {facturas.map((f) => {
                  const open = !!openFacturas[f.id];
                  return (
                    <React.Fragment key={f.id}>
                      <div className={`fin-tr fin-cols-facturas ${f.es_anulada ? 'fin-tr--void' : ''} ${open ? 'fin-tr--open' : ''}`}>
                        <div className="fin-td">
                          <button
                            type="button"
                            className="fin-row-toggle"
                            aria-expanded={open}
                            aria-label={open ? `Cerrar factura ${f.invoice_number}` : `Abrir factura ${f.invoice_number}`}
                            onClick={() => toggleFactura(f.id)}
                          >
                            {open ? '▾' : '▸'}
                          </button>
                        </div>
                        <div className="fin-td fin-td--strike" data-label="Factura">{textOrDash(f.invoice_number)}</div>
                        <div className="fin-td" data-label="Cliente">{textOrDash(f.cliente)}</div>
                        <div className="fin-td" data-label="Tipo"><TipoChip tipo={f.tipo} /></div>
                        <div className="fin-td" data-label="Estado"><StatusChip status={f.status} /></div>
                        <div className="fin-td fin-td--num fin-td--strike" data-label="Total">{fmtUsd(f.total)}</div>
                        <div className="fin-td fin-td--num" data-label="Saldo">{fmtUsd(f.saldo)}</div>
                      </div>
                      {open && (
                        <FacturaDetalle
                          f={f}
                          cajaOpen={!!openCajas[f.id]}
                          onToggleCaja={() => toggleCaja(f)}
                          cajaEntry={cajas[cajaKey(f)]}
                        />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            )}
          </section>

          {excepciones.error && <ErrorBanner message={excepciones.error} onRetry={excepciones.refetch} />}
          {excepcionesManifiesto.length > 0 && (
            <section className="fin-section">
              <h3 className="fin-section-title">Excepciones en este manifiesto</h3>
              <div className="fin-table">
                <div className="fin-tr fin-tr--head fin-cols-exc">
                  <div className="fin-td">Factura</div>
                  <div className="fin-td">Cliente</div>
                  <div className="fin-td">Motivos</div>
                  <div className="fin-td fin-td--num">Saldo</div>
                </div>
                {excepcionesManifiesto.map((e) => (
                  <div key={e.id} className="fin-tr fin-cols-exc">
                    <div className="fin-td" data-label="Factura">{textOrDash(e.invoice_number)}</div>
                    <div className="fin-td" data-label="Cliente">{textOrDash(e.cliente)}</div>
                    <div className="fin-td" data-label="Motivos">
                      <div className="fin-motivos">
                        {e.motivos.map((mot) => <span key={mot} className="fin-chip fin-chip--motivo">{mot}</span>)}
                      </div>
                    </div>
                    <div className="fin-td fin-td--num" data-label="Saldo">{fmtUsd(e.saldo)}</div>
                  </div>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
};

export default ManifiestoTab;
